import React from 'react';
import { PhoneIcon, MailIcon, MapPinIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function ContactPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-1129">
      <SEOHead title="Contact Us | Book a Free Strategy Call | Viridia Analytics Phoenix" description="Book a free strategy call with Viridia Analytics. We'll review your current marketing, identify opportunities, and show you how to turn more leads into booked jobs. Call (623) 300-0708." canonical="/contact" keywords="contact Viridia Analytics, free strategy call, marketing consultation Phoenix, book a call, Phoenix marketing agency contact" data-id="element-1130" />
      {/* HERO SECTION */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center" data-id="element-1131">
        <ScrollReveal data-id="element-1132">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight" data-id="element-1133">
            Book a strategy call - no pitch, just a real look at your leads
          </h1>
          <p className="text-xl text-viridia font-medium max-w-2xl mx-auto mb-4" data-id="element-1134">
            Our clients generated 380+ leads and 57 AI-booked estimates last
            month. Let us show you what that could look like for your business.
          </p>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-1135">
            Booking an appointment is fast and easy
          </p>
        </ScrollReveal>
      </section>

      {/* MAIN CONTENT - BOOKING & EXPECTATIONS */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-1136">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8" data-id="element-1137">
          {/* Left Column: What to Expect */}
          <ScrollReveal direction="right" className="lg:col-span-5" data-id="element-1138">
            <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 h-full" data-id="element-1139">
              <h2 className="text-2xl font-bold text-white mb-8" data-id="element-1140">
                What Happens on the Call
              </h2>

              <div className="space-y-8 mb-12" data-id="element-1141">
                <div className="flex" data-id="element-1142">
                  <div className="flex-shrink-0 mr-4" data-id="element-1143">
                    <div className="w-8 h-8 rounded-full bg-viridia text-white flex items-center justify-center font-bold" data-id="element-1144">
                      1
                    </div>
                  </div>
                  <div data-id="element-1145">
                    <h3 className="text-lg font-semibold text-white mb-1" data-id="element-1146">
                      We review your current marketing
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed" data-id="element-1147">
                      We'll look at your ads, website, and follow-up process to
                      find quick wins.
                    </p>
                  </div>
                </div>

                <div className="flex" data-id="element-1148">
                  <div className="flex-shrink-0 mr-4" data-id="element-1149">
                    <div className="w-8 h-8 rounded-full bg-viridia text-white flex items-center justify-center font-bold" data-id="element-1150">
                      2
                    </div>
                  </div>
                  <div data-id="element-1151">
                    <h3 className="text-lg font-semibold text-white mb-1" data-id="element-1152">
                      We uncover growth opportunities
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed" data-id="element-1153">
                      Based on your market and competition, we'll identify where
                      you're leaving money on the table.
                    </p>
                  </div>
                </div>

                <div className="flex" data-id="element-1154">
                  <div className="flex-shrink-0 mr-4" data-id="element-1155">
                    <div className="w-8 h-8 rounded-full bg-viridia text-white flex items-center justify-center font-bold" data-id="element-1156">
                      3
                    </div>
                  </div>
                  <div data-id="element-1157">
                    <h3 className="text-lg font-semibold text-white mb-1" data-id="element-1158">
                      You get a custom game plan
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed" data-id="element-1159">
                      Walk away with a clear roadmap - whether you work with us
                      or not. No obligation.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-gray-800" data-id="element-1160">
                <div className="flex items-center mb-4" data-id="element-1161">
                  <div className="w-12 h-12 rounded-full bg-viridia-black border-2 border-viridia flex items-center justify-center mr-4" data-id="element-1162">
                    <span className="text-white font-bold" data-id="element-1163">KR</span>
                  </div>
                  <div data-id="element-1164">
                    <p className="text-white font-semibold" data-id="element-1165">
                      Kevin Rogers - Viridia Analytics
                    </p>
                    <p className="text-viridia text-sm" data-id="element-1166">
                      30 Minute Strategy Call
                    </p>
                  </div>
                </div>
                <div className="space-y-2 text-gray-400 text-sm ml-16" data-id="element-1167">
                  <p className="flex items-center" data-id="element-1168">
                    <PhoneIcon className="w-4 h-4 mr-2" data-id="element-1169" /> (623) 300-0708
                  </p>
                  <p className="flex items-center" data-id="element-1170">
                    <MapPinIcon className="w-4 h-4 mr-2" data-id="element-1171" /> Phoenix, Arizona
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>

          {/* Right Column: Booking Widget */}
          <ScrollReveal direction="left" delay={0.2} className="lg:col-span-7" data-id="element-1172">
            <div className="bg-viridia-surface rounded-2xl border border-gray-800 overflow-hidden shadow-2xl h-full min-h-[900px]" data-id="element-1173">
              <iframe src="https://api.viridiaanalytics.com/widget/booking/hv5pIfS27zbJCMMGNnkJ" style={{
              width: '100%',
              height: '100%',
              minHeight: '900px',
              border: 'none'
            }} title="Book a Call" data-id="element-1174" />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CONTACT INFO BAR */}
      <section className="py-12 bg-viridia-black border-t border-gray-800" data-id="element-1175">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1176">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center" data-id="element-1177">
            <div className="flex flex-col items-center" data-id="element-1178">
              <div className="w-12 h-12 bg-viridia-surface rounded-full flex items-center justify-center mb-4 border border-gray-800" data-id="element-1179">
                <PhoneIcon className="w-5 h-5 text-viridia" data-id="element-1180" />
              </div>
              <p className="text-white font-medium" data-id="element-1181">(623) 300-0708</p>
            </div>
            <div className="flex flex-col items-center" data-id="element-1182">
              <div className="w-12 h-12 bg-viridia-surface rounded-full flex items-center justify-center mb-4 border border-gray-800" data-id="element-1183">
                <MailIcon className="w-5 h-5 text-viridia" data-id="element-1184" />
              </div>
              <p className="text-white font-medium" data-id="element-1185">
                info@viridiaanalytics.com
              </p>
            </div>
            <div className="flex flex-col items-center" data-id="element-1186">
              <div className="w-12 h-12 bg-viridia-surface rounded-full flex items-center justify-center mb-4 border border-gray-800" data-id="element-1187">
                <MapPinIcon className="w-5 h-5 text-viridia" data-id="element-1188" />
              </div>
              <p className="text-white font-medium" data-id="element-1189">Phoenix, Arizona</p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}